import { apiImpresoraListado } from '../api/apiImpresoraListado.ts'
import { ImpresoraProps } from '../interfaces/impresora.ts'
import type { NetworkPrinter, PrinterInfo, SyncNetworkPrintersResult } from '../sdk/isi-printer-client.ts'
import { isiPrint } from './isiPrintClient.ts'

/**
 * Impresoras de red de la sucursal (isi-print v1.3.2+).
 *
 * - Se registran una sola vez en el ERP, por sucursal: `atributo1` = IP:puerto y
 *   `atributo2` = ajustes de isi-print en JSON (papel, columnas, tabla de caracteres y espera).
 * - Al iniciar sesión (verificación de isi-print) y después de guardar o eliminar una, el front
 *   entrega la lista completa a isi-print con `syncNetworkPrinters`: agrega las nuevas, actualiza
 *   las que cambiaron y quita las que la sucursal ya no tiene.
 * - `getPrinters()` las devuelve junto con las USB de la PC (`type: 'network'`, `managed: true`,
 *   `id` = IP:puerto), así que se imprime con el mismo `atributo1`.
 */

/** Puerto por defecto de las impresoras de red (RAW / JetDirect) */
export const IMPRESORA_RED_PUERTO_DEFAULT = 9100

/** IPv4 con puerto opcional. Ej. 192.168.1.100 o 192.168.1.100:9100 */
export const IMPRESORA_IP_PUERTO_REGEX =
  /^((25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)\.){3}(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)(:([1-9]\d{0,3}|[1-5]\d{4}|6[0-4]\d{3}|65[0-4]\d{2}|655[0-2]\d|6553[0-5]))?$/

/** Indica si el valor de atributo1 corresponde a una impresora de red (IP[:puerto]) */
export const esImpresoraRed = (atributo1?: string | null) =>
  IMPRESORA_IP_PUERTO_REGEX.test((atributo1 ?? '').trim())

/** IP con el puerto explícito (192.168.1.50 → 192.168.1.50:9100), igual que el `id` de isi-print */
export const normalizarDireccionRed = (valor?: string | null) => {
  const v = (valor ?? '').trim()
  if (!IMPRESORA_IP_PUERTO_REGEX.test(v)) return v
  return v.includes(':') ? v : `${v}:${IMPRESORA_RED_PUERTO_DEFAULT}`
}

/** Por IP directa isi-print solo envía tickets ESC/POS: solo rollos */
export type ImpresoraRedPapel = NonNullable<NetworkPrinter['paper']>
export type ImpresoraRedCodepage = NonNullable<NetworkPrinter['codepage']>

/** Ajustes de isi-print de una impresora de red, guardados en `atributo2` */
export interface ImpresoraRedConfig {
  paper: ImpresoraRedPapel
  chars_per_line?: number
  codepage?: ImpresoraRedCodepage
  network_retry_seconds?: number
}

export const IMPRESORA_RED_NOMBRE_MAX = 60
export const IMPRESORA_RED_COLUMNAS_MIN = 16
export const IMPRESORA_RED_COLUMNAS_MAX = 96
export const IMPRESORA_RED_ESPERA_MIN = 5
export const IMPRESORA_RED_ESPERA_MAX = 120
/** Espera de isi-print si no se indica */
export const IMPRESORA_RED_ESPERA_DEFAULT = 12

export const IMPRESORA_RED_PAPEL_OPCIONES: {
  value: ImpresoraRedPapel
  label: string
  /** Columnas por defecto del papel */
  columnas: number
  paperSize: '80mm' | '58mm'
}[] = [
  { value: 'rollo-80', label: 'Rollo 80 mm', columnas: 48, paperSize: '80mm' },
  { value: 'rollo-58', label: 'Rollo 58 mm', columnas: 32, paperSize: '58mm' },
]

export const IMPRESORA_RED_CODEPAGE_OPCIONES: { value: ImpresoraRedCodepage; label: string }[] = [
  { value: 'cp850', label: 'CP850 · Latino (tildes, ñ, ¿, ¡)' },
  { value: 'cp858', label: 'CP858 · Latino con €' },
  { value: 'wpc1252', label: 'WPC1252 · Windows Latino' },
  { value: 'cp437', label: 'CP437 · EE. UU.' },
  { value: 'ascii', label: 'ASCII · sin tildes' },
]

export const IMPRESORA_RED_CONFIG_DEFAULT: ImpresoraRedConfig = { paper: 'rollo-80' }

const enRango = (n: unknown, min: number, max: number): number | undefined =>
  typeof n === 'number' && Number.isInteger(n) && n >= min && n <= max ? n : undefined

/** Ajustes guardados en `atributo2`; si no hay o no es JSON válido, los valores por defecto */
export const leerConfigRed = (atributo2?: string | null): ImpresoraRedConfig => {
  let raw: Record<string, unknown> = {}
  try {
    const parsed = JSON.parse(atributo2 || '{}')
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) raw = parsed
  } catch {
    // atributo2 con texto libre (registro antiguo): valores por defecto
  }
  const paper = IMPRESORA_RED_PAPEL_OPCIONES.some((o) => o.value === raw.paper)
    ? (raw.paper as ImpresoraRedPapel)
    : IMPRESORA_RED_CONFIG_DEFAULT.paper
  const codepage = IMPRESORA_RED_CODEPAGE_OPCIONES.some((o) => o.value === raw.codepage)
    ? (raw.codepage as ImpresoraRedCodepage)
    : undefined
  return limpiarConfigRed({
    paper,
    chars_per_line: enRango(raw.chars_per_line, IMPRESORA_RED_COLUMNAS_MIN, IMPRESORA_RED_COLUMNAS_MAX),
    codepage,
    network_retry_seconds: enRango(
      raw.network_retry_seconds,
      IMPRESORA_RED_ESPERA_MIN,
      IMPRESORA_RED_ESPERA_MAX,
    ),
  })
}

/** Quita las claves vacías para no enviar `undefined` a isi-print ni guardarlas en el ERP */
const limpiarConfigRed = (config: ImpresoraRedConfig): ImpresoraRedConfig => {
  const r: ImpresoraRedConfig = { paper: config.paper }
  if (config.chars_per_line) r.chars_per_line = config.chars_per_line
  if (config.codepage) r.codepage = config.codepage
  if (config.network_retry_seconds) r.network_retry_seconds = config.network_retry_seconds
  return r
}

/** Ajustes → `atributo2` (JSON compacto) */
export const serializarConfigRed = (config: ImpresoraRedConfig) => JSON.stringify(limpiarConfigRed(config))

/** Resumen legible, ej. "Rollo 80 mm · 48 col. · CP850 · espera 30 s" */
export const textoConfigRed = (config: ImpresoraRedConfig) => {
  const papel = IMPRESORA_RED_PAPEL_OPCIONES.find((o) => o.value === config.paper)
  return [
    papel?.label ?? config.paper,
    `${config.chars_per_line ?? papel?.columnas ?? 48} col.`,
    config.codepage?.toUpperCase(),
    config.network_retry_seconds ? `espera ${config.network_retry_seconds} s` : null,
  ]
    .filter(Boolean)
    .join(' · ')
}

/** Impresora del ERP → impresora de red para isi-print; null si no es de red */
export const impresoraToNetworkPrinter = (impresora: ImpresoraProps): NetworkPrinter | null => {
  if (!esImpresoraRed(impresora.atributo1)) return null
  return {
    name: (impresora.nombre || impresora.atributo1 || '').trim().slice(0, IMPRESORA_RED_NOMBRE_MAX),
    address: normalizarDireccionRed(impresora.atributo1),
    ...leerConfigRed(impresora.atributo2),
  }
}

/** Impresoras de red de la sucursal, sin direcciones repetidas y ordenadas por nombre */
export const impresorasRedDeSucursal = (impresoras: ImpresoraProps[]): NetworkPrinter[] => {
  const porDireccion = new Map<string, NetworkPrinter>()
  impresoras.forEach((i) => {
    const p = impresoraToNetworkPrinter(i)
    if (p && !porDireccion.has(p.address)) porDireccion.set(p.address, p)
  })
  return [...porDireccion.values()].sort((a, b) => a.name.localeCompare(b.name))
}

/** Impresora de red de la sucursal entregada por el ERP (no USB ni registrada a mano en el panel) */
export const esImpresoraRedSucursal = (p: PrinterInfo) => p.type === 'network' && Boolean(p.managed)

/** Cada cuánto se vuelve a pedir la lista al backend en las verificaciones automáticas */
const LISTA_TTL_MS = 5 * 60_000

let listaCache: { sucursal: number; lista: NetworkPrinter[]; leidaEn: number } | null = null
/** Última lista entregada a isi-print en esta sesión del navegador */
let ultimaFirma: string | null = null

const firmaRed = (sucursal: number, lista: NetworkPrinter[]) => `${sucursal}|${JSON.stringify(lista)}`

/** isi-print tiene exactamente las impresoras de la sucursal (detecta un agente reinstalado o reiniciado) */
const agenteAlDia = (lista: NetworkPrinter[], enAgente: PrinterInfo[]) => {
  const ids = new Set(enAgente.filter(esImpresoraRedSucursal).map((p) => p.id.trim().toLowerCase()))
  return ids.size === lista.length && lista.every((p) => ids.has(p.address.toLowerCase()))
}

export interface SincronizarImpresorasRedOptions {
  /** Pide la lista al backend y la entrega aunque no haya cambios (después de guardar o eliminar) */
  forzar?: boolean
  /** Impresoras del ERP ya consultadas; evita pedirlas al backend */
  impresoras?: ImpresoraProps[]
  /** Impresoras que hoy lista isi-print, para detectar si le faltan o le sobran */
  enAgente?: PrinterInfo[]
}

/**
 * Entrega a isi-print las impresoras de red de la sucursal. Sin `forzar`, solo llama al agente si
 * la lista cambió desde la última entrega o si el agente no las tiene (ej. lo reinstalaron).
 * Lanza error si el backend o el agente fallan (isi-print anterior a v1.3.2 no tiene el endpoint).
 * @returns Resultado del agente, o null si no hizo falta sincronizar
 */
export const sincronizarImpresorasRed = async (
  codigoSucursal: number,
  { forzar = false, impresoras, enAgente }: SincronizarImpresorasRedOptions = {},
): Promise<SyncNetworkPrintersResult | null> => {
  const vigente =
    !forzar &&
    !impresoras &&
    listaCache?.sucursal === codigoSucursal &&
    Date.now() - listaCache.leidaEn < LISTA_TTL_MS
  const lista = vigente
    ? listaCache!.lista
    : impresorasRedDeSucursal(impresoras ?? (await apiImpresoraListado({ codigoSucursal })))
  if (!vigente) listaCache = { sucursal: codigoSucursal, lista, leidaEn: Date.now() }

  const firma = firmaRed(codigoSucursal, lista)
  if (!forzar && firma === ultimaFirma && (!enAgente || agenteAlDia(lista, enAgente))) return null

  const r = await isiPrint.syncNetworkPrinters(lista)
  ultimaFirma = firma
  return r
}

/** El agente agregó, actualizó o quitó alguna impresora */
export const huboCambiosRed = (r: SyncNetworkPrintersResult | null) =>
  Boolean(r && (r.added?.length || r.updated?.length || r.removed?.length))
